import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Avatar,
  CssBaseline,
  Link,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/';

/**
 * Public forgot-password — request reset email (AsroTrack-style).
 */
const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}synctrack/user/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.error || data.message || `Failed (${res.status})`);
      }
      setSent(true);
      toast.success(data.message || 'If that account exists, a reset link was sent.');
    } catch (err) {
      setError(err.message || 'Failed to send reset email');
      toast.error(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
          <LockResetIcon />
        </Avatar>
        <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
          Forgot password
        </Typography>
        <Paper elevation={2} sx={{ p: 3, width: '100%' }}>
          {sent ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                If an account exists for that email, a password reset link has been sent. Check your inbox.
              </Alert>
              <Button fullWidth variant="contained" onClick={() => navigate('/')}>
                Back to login
              </Button>
            </>
          ) : (
            <Box component="form" onSubmit={onSubmit}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the email for your account. We&apos;ll send a link to reset your password.
              </Typography>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                autoComplete="email"
                autoFocus
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ mt: 2, mb: 1 }}
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
              <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                <Link component={RouterLink} to="/" underline="hover">
                  Back to login
                </Link>
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPassword;
